// Map renderer: owns the canvas, camera, terrain cache and frame loop.
// Draws only when something changed (idle = 0 fps). docs/07-ARCHITECTURE.md → Rendering.
import type { MapData } from '../sim/types';
import {
  FLING_START,
  FLING_STOP,
  NO_INSETS,
  capFling,
  clampCamera,
  fitCamera,
  openingCamera,
  flingSpeed,
  flingStep,
  panBy,
  screenToWorld,
  zoomAt,
  zoomBucket,
  zoomLimits,
  type Camera,
  type Insets,
  type Velocity,
  type Viewport,
} from './camera';
import { mapBounds, type Bounds } from './hexgeom';
import { drawDebug, drawGrid } from './layers/grid';
import { drawTerrain, outlinePath } from './layers/terrain';
import { buildTerrain, type TerrainGeometry } from './terrainGeometry';
import { DAY, type Ink } from './theme';

export type RenderOptions = { grid: boolean; debug: boolean };

/** Largest terrain cache, in device pixels (≈32 MB). Beyond it terrain is drawn live. */
export const MAX_CACHE_PIXELS = 8_000_000;
const CACHE_PAD = 0.3; // world units around the map, for the border stroke
const MAX_DPR = 3;
const OUTSIDE_ALPHA = 0.05;

type Cache = { canvas: HTMLCanvasElement; bucket: number; w: number; h: number };

export class MapRenderer {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private vp: Viewport = { w: 1, h: 1 };
  private dpr = 1;
  private cam: Camera = { x: 0, y: 0, scale: 30 };
  private map: MapData | null = null;
  private geo: TerrainGeometry | null = null;
  private bounds: Bounds = { minX: 0, minY: 0, maxX: 1, maxY: 1 };
  private ink: Ink = DAY;
  private opts: RenderOptions = { grid: false, debug: false };
  private insets: Insets = NO_INSETS;
  private cache: Cache | null = null;
  private interacting = false;
  private fling: Velocity | null = null;
  private lastT = 0;
  private raf = 0;
  /** Frames drawn so far (tests use it to check the idle loop stops). */
  frames = 0;
  /** Terrain cache rebuilds so far. */
  cacheBuilds = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D not available');
    this.ctx = ctx;
  }

  // ---------- setup ----------

  setMap(map: MapData): void {
    this.map = map;
    this.geo = buildTerrain(map);
    this.bounds = mapBounds(map.width, map.height);
    this.cache = null;
    this.cam = this.clamp(openingCamera(this.vp, this.bounds, this.insets));
    this.requestDraw();
  }

  resize(w: number, h: number, dpr: number): void {
    const d = Math.min(MAX_DPR, Math.max(1, dpr));
    const bw = Math.max(1, Math.round(w * d));
    const bh = Math.max(1, Math.round(h * d));
    const first = this.vp.w <= 1;
    this.vp = { w, h };
    if (d !== this.dpr) this.cache = null;
    this.dpr = d;
    if (this.canvas.width !== bw) this.canvas.width = bw;
    if (this.canvas.height !== bh) this.canvas.height = bh;
    if (this.map && first) this.cam = this.clamp(openingCamera(this.vp, this.bounds, this.insets));
    this.cam = this.clamp(this.cam);
    this.requestDraw();
  }

  setInsets(insets: Insets): void {
    this.insets = insets;
  }

  setInk(ink: Ink): void {
    this.ink = ink;
    this.cache = null;
    this.requestDraw();
  }

  setOptions(o: Partial<RenderOptions>): void {
    this.opts = { ...this.opts, ...o };
    this.requestDraw();
  }

  /** Throw away the terrain cache (e.g. once web fonts finish loading). */
  invalidate(): void {
    this.cache = null;
    this.requestDraw();
  }

  destroy(): void {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.cache = null;
  }

  // ---------- camera ----------

  get camera(): Camera {
    return { ...this.cam };
  }

  get viewport(): Viewport {
    return { ...this.vp };
  }

  get limits(): { min: number; max: number } {
    return zoomLimits(this.vp, this.bounds, this.insets);
  }

  setCamera(cam: Camera): void {
    this.cam = this.clamp(cam);
    this.requestDraw();
  }

  fit(): void {
    this.fling = null;
    this.cam = fitCamera(this.vp, this.bounds, this.insets);
    this.requestDraw();
  }

  panBy(dx: number, dy: number): void {
    this.cam = this.clamp(panBy(this.cam, dx, dy));
    this.requestDraw();
  }

  zoomAt(sx: number, sy: number, factor: number): void {
    this.cam = this.clamp(zoomAt(this.cam, this.vp, { x: sx, y: sy }, factor, this.limits));
    this.requestDraw();
  }

  /** A finger went down: stop any glide and keep the cache until the gesture ends. */
  beginGesture(): void {
    this.fling = null;
    this.interacting = true;
  }

  /** All fingers up. `v` is the release velocity in px/ms. */
  endGesture(v: Velocity | null): void {
    this.interacting = false;
    if (v && flingSpeed(v) >= FLING_START) {
      this.fling = capFling(v);
      this.lastT = 0;
    }
    this.requestDraw();
  }

  get gliding(): boolean {
    return this.fling !== null;
  }

  private clamp(cam: Camera): Camera {
    return clampCamera(cam, this.bounds, this.limits);
  }

  // ---------- frame loop ----------

  requestDraw(): void {
    if (this.raf) return;
    this.raf = requestAnimationFrame((t) => this.frame(t));
  }

  private frame(t: number): void {
    this.raf = 0;
    if (this.fling) {
      const dt = this.lastT === 0 ? 16 : Math.min(50, t - this.lastT);
      this.lastT = t;
      const step = flingStep(this.fling, dt);
      const before = this.cam;
      this.cam = this.clamp(panBy(this.cam, step.dx, step.dy));
      const stuck = before.x === this.cam.x && before.y === this.cam.y;
      this.fling = flingSpeed(step.v) < FLING_STOP || stuck ? null : step.v;
      this.requestDraw();
    }
    this.draw();
  }

  private draw(): void {
    const { ctx, cam, vp, dpr } = this;
    this.frames++;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    ctx.fillStyle = this.ink.paper;
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    const geo = this.geo;
    const map = this.map;
    if (!geo || !map) return;

    const s = cam.scale * dpr;
    ctx.setTransform(
      s,
      0,
      0,
      s,
      (vp.w / 2 - cam.x * cam.scale) * dpr,
      (vp.h / 2 - cam.y * cam.scale) * dpr,
    );
    const tl = screenToWorld(cam, vp, { x: 0, y: 0 });
    const br = screenToWorld(cam, vp, { x: vp.w, y: vp.h });
    const view: Bounds = { minX: tl.x, minY: tl.y, maxX: br.x, maxY: br.y };
    const cssPx = 1 / cam.scale;

    // Off-map area: faint ink wash so the map edge reads.
    ctx.save();
    ctx.globalAlpha = OUTSIDE_ALPHA;
    ctx.fillStyle = this.ink.ink;
    outlinePath(ctx, geo);
    ctx.rect(view.minX - 1, view.minY - 1, view.maxX - view.minX + 2, view.maxY - view.minY + 2);
    ctx.fill('evenodd');
    ctx.restore();

    const cache = this.ensureCache(s);
    if (cache) {
      ctx.imageSmoothingEnabled = true;
      ctx.drawImage(
        cache.canvas,
        this.bounds.minX - CACHE_PAD,
        this.bounds.minY - CACHE_PAD,
        cache.w / cache.bucket,
        cache.h / cache.bucket,
      );
    } else {
      drawTerrain(ctx, geo, { ...this.ink, cssPx });
    }

    if (this.opts.grid) drawGrid(ctx, map, view, cssPx, this.ink);
    if (this.opts.debug) drawDebug(ctx, map, view, cssPx, this.ink);
  }

  /** The terrain bitmap for this zoom, rebuilt only when the zoom bucket changes. */
  private ensureCache(devScale: number): Cache | null {
    const bucket = zoomBucket(devScale);
    const b = this.bounds;
    const w = Math.ceil((b.maxX - b.minX + 2 * CACHE_PAD) * bucket);
    const h = Math.ceil((b.maxY - b.minY + 2 * CACHE_PAD) * bucket);
    if (w * h > MAX_CACHE_PIXELS) {
      // Too big to cache: keep a scaled old cache while a pinch is under way, else draw live.
      if (this.interacting && this.cache) return this.cache;
      return null;
    }
    const c = this.cache;
    if (c && c.bucket === bucket) return c;
    // Mid-gesture, reuse a nearby cache rather than stall a frame on a rebuild.
    if (c && this.interacting && c.bucket / bucket < 2 && bucket / c.bucket < 2) return c;
    if (c && this.fling) return c;
    const canvas = c?.canvas ?? document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const cx = canvas.getContext('2d');
    if (!cx) return null;
    cx.setTransform(1, 0, 0, 1, 0, 0);
    cx.clearRect(0, 0, w, h);
    cx.setTransform(
      bucket,
      0,
      0,
      bucket,
      (CACHE_PAD - b.minX) * bucket,
      (CACHE_PAD - b.minY) * bucket,
    );
    drawTerrain(cx, this.geo as TerrainGeometry, { ...this.ink, cssPx: this.dpr / bucket });
    this.cache = { canvas, bucket, w, h };
    this.cacheBuilds++;
    return this.cache;
  }
}
